import { CookieManager } from '@Shared/storage/cookie_manager';
import { LoggerService } from '@Shared/logger';
import { MNGAuthentication, STORAGE } from '@Core';
import { MNGSession } from '@Modules/index';

const SS_PAYWALL_COOKIE_NAME = 'vip-go-seg';
const SS_PAYWALL_COOKIE_VALUE_PREFIX = 'vc-v1__has_access';

// Called before the entitlement pipeline has had a chance to run (e.g. ad code
// querying isUserAdFree() immediately on page load), so it can't rely on
// EntitlementService's async code
function readLocalAdFreeStatus(): boolean {
  const session = AuthenticationUtils.getUserDataFromStorage();
  if (!session) return false;
  const hasIdentity =
    session.idToken || window.MNGAuthentication?.authenticationReady?.idToken;
  return hasIdentity && session.adFree === true;
}

const EmptyMNGAuthentication: MNGAuthentication = {
  authenticationReady: undefined,
  entitlementsReady: undefined,
  // isUserAdFree: () => false,
  // isUserSubscriber: () => false,
  logoutCallbacks: [],
  postAuthEvents: [],
  preAuthGTMEvents: [],
  customLogout: () => Promise.resolve(),
};

export class AuthenticationUtils {
  private static Logger = LoggerService.createLogger('AuthenticationUtils');

  static removeSSPaywallCookie(): void {
    const value = CookieManager.getCookie(SS_PAYWALL_COOKIE_NAME);
    if (
      typeof value === 'string' &&
      value.startsWith(SS_PAYWALL_COOKIE_VALUE_PREFIX)
    ) {
      this.Logger.trace('SS paywall cookie found, removing it');
      CookieManager.deleteAllCookiesByName(SS_PAYWALL_COOKIE_NAME);
    }
  }

  // Reads whatever MNGSession was cached in localStorage on a previous page
  // load. Useful for anything that needs session data before/without going
  // through EntitlementService's async checkEntitlements() flow.
  static getUserDataFromStorage(): MNGSession | null {
    try {
      const raw = window.localStorage.getItem(STORAGE.sessionLocalStorageKey);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  static initializeMNGAuthenticationGlobal(clearExistingValues = false) {
    const existingValues = clearExistingValues
      ? {}
      : window.MNGAuthentication || {};
    window.MNGAuthentication = {
      ...EmptyMNGAuthentication,
      ...existingValues,
    };
  }

  // NOTE: fireLegacyAuthenticationReadyEvent used to live here. It re-derived auth
  // state from the Auth0 SDK alone and dispatched a SECOND `authenticationReady`,
  // which on a session recovered from the session server contradicted (and, being
  // later, overrode) the one AuthCompatService dispatches off onAuthDetermined.
  // AuthCompatService is now the single producer — see CheckAuthStatusStep.
  //
  // Its only non-duplicate behaviour was a BlueConic LOWA promotion, which was
  // unreachable: `blueconic.enabled` defaults to false and no site config sets it.
  // The live LOWA implementation is the legacy WordPress one in
  // mng-digisubs/assets/js/authentication/auth.js. MNGAuth.Blueconic still exposes
  // bcLowaCheck()/bcGetAuth0Id() for callers that want to run it explicitly.

  /**
   * Triggers Connext logout and waits briefly so its redirect can start
   * before Auth0 logout runs (both involve redirects; they cannot run in parallel).
   */
  static callConnextLogoutWithTimeout(timeoutMs = 300): Promise<void> {
    return new Promise((resolve) => {
      AuthenticationUtils.Logger.trace('About to call Connext Logout...');
      window.Connext?.Logout();
      setTimeout(() => resolve(), timeoutMs);
    });
  }
}
